import React, { useState } from 'react';
import type { InvoiceEnrollmentViewProps } from 'interfaces/enrollments/InvoiceEnrollmentViewProps';
import { InvoiceStatus } from 'models/payments/InvoiceStatus';
import { ApiService } from 'services/ApiService';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatCurrency = (minorUnits: number, currency: string): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(minorUnits / 100);

const STATUS_LABEL: Record<InvoiceStatus, string> = {
  [InvoiceStatus.Draft]: 'Being Prepared',
  [InvoiceStatus.Open]: 'Payment Due',
  [InvoiceStatus.Paid]: 'Paid',
  [InvoiceStatus.Void]: 'Voided',
  [InvoiceStatus.Uncollectible]: 'Uncollectible',
};

const STATUS_BADGE: Record<InvoiceStatus, string> = {
  [InvoiceStatus.Draft]: 'bg-gray-100 text-gray-600 border border-gray-300',
  [InvoiceStatus.Open]: 'bg-yellow-100 text-yellow-700 border border-yellow-300',
  [InvoiceStatus.Paid]: 'bg-green-100 text-green-700 border border-green-300',
  [InvoiceStatus.Void]: 'bg-gray-100 text-gray-500 border border-gray-300',
  [InvoiceStatus.Uncollectible]: 'bg-red-100 text-red-700 border border-red-300',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const InvoiceEnrollmentView: React.FC<InvoiceEnrollmentViewProps> = ({
  enrollment,
  isDisabled = false,
  onStatusRefresh,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const status = enrollment.localInvoiceStatus;
  const awaitingTeam = enrollment.isAwaitingTeamInput;
  const hasDepositCredit = enrollment.depositCreditMinorUnits > 0;
  const currencyCode = enrollment.currency.toUpperCase();

  const handlePayInvoice = async () => {
    setLoading(true);
    setError(null);
    try {
      const { checkoutUrl } = await ApiService.createInvoiceCheckoutSession(enrollment.id);
      // Same-tab navigation so Stripe's success/cancel redirect lands back in this app
      // (see InvoiceReturnPage) instead of leaving the customer stranded in a new tab.
      window.location.assign(checkoutUrl);
    } catch {
      setError('Unable to start the payment process. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-lg font-semibold text-gray-800">Invoice</h2>
        <span
          className={`inline-flex items-center rounded-full px-3 py-0.5 text-xs font-semibold ${
            awaitingTeam ? 'bg-blue-100 text-blue-700 border border-blue-300' : STATUS_BADGE[status]
          }`}
          aria-label={`Invoice status: ${awaitingTeam ? 'Being Prepared' : STATUS_LABEL[status]}`}
        >
          {awaitingTeam ? 'Being Prepared' : STATUS_LABEL[status]}
        </span>
      </div>

      {/* Awaiting team input — no amounts are shown until the team finalizes */}
      {awaitingTeam && (
        <div className="flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-700">
          <svg className="h-4 w-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Your artist is preparing this invoice. Check back shortly or refresh your status below.</span>
        </div>
      )}

      {/* Description */}
      {!awaitingTeam && enrollment.description && (
        <p className="text-sm text-gray-600">{enrollment.description}</p>
      )}

      {/* Amount breakdown — hidden until the team finalizes the per-client details */}
      {!awaitingTeam && (
      <div className="rounded-lg bg-gray-50 border border-gray-200 divide-y divide-gray-200">
        <div className="flex items-center justify-between px-4 py-2.5 text-sm">
          <span className="text-gray-600">Subtotal</span>
          <span className="font-medium text-gray-800">
            {formatCurrency(enrollment.totalMinorUnits, enrollment.currency)}
          </span>
        </div>
        {hasDepositCredit && (
          <div className="flex items-center justify-between px-4 py-2.5 text-sm">
            <span className="text-gray-600">Deposit credit applied</span>
            <span className="font-medium text-green-700">
              &minus;{formatCurrency(enrollment.depositCreditMinorUnits, enrollment.currency)}
            </span>
          </div>
        )}
        <div className="flex items-center justify-between px-4 py-2.5 text-sm font-semibold">
          <span className="text-gray-900">Amount due</span>
          <span className="text-gray-900 text-base">
            {formatCurrency(enrollment.amountDueMinorUnits, enrollment.currency)}{' '}
            <span className="text-xs font-normal text-gray-500">{currencyCode}</span>
          </span>
        </div>
      </div>
      )}

      {/* Status-specific messaging */}
      {!awaitingTeam && status === InvoiceStatus.Draft && (
        <div className="flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-700">
          <svg className="h-4 w-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20A10 10 0 0012 2z" />
          </svg>
          <span>Your invoice is being finalized. You will be notified when it is ready for payment.</span>
        </div>
      )}

      {status === InvoiceStatus.Paid && (
        <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
          <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span>Invoice paid in full. Thank you!</span>
        </div>
      )}

      {status === InvoiceStatus.Void && (
        <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-600">
          <p>This invoice has been voided. Please contact your artist if you have any questions.</p>
        </div>
      )}

      {status === InvoiceStatus.Uncollectible && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          <p className="font-medium">Payment not collected</p>
          <p>This invoice has been marked uncollectible. Please contact your artist to resolve this.</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <p className="text-sm text-red-600" role="alert">{error}</p>
      )}

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3 pt-1">
        {!awaitingTeam && status === InvoiceStatus.Open && (
          <button
            type="button"
            onClick={handlePayInvoice}
            disabled={isDisabled || loading}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading && (
              <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            )}
            {loading ? 'Redirecting…' : 'View & Pay Invoice'}
          </button>
        )}
        {onStatusRefresh && (
          <button
            type="button"
            onClick={onStatusRefresh}
            className="text-sm text-gray-500 hover:text-gray-700 underline focus:outline-none focus:ring-2 focus:ring-gray-400 rounded"
          >
            Refresh status
          </button>
        )}
      </div>
    </div>
  );
};

export default InvoiceEnrollmentView;
